import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Minus, Upload, Trash2 } from "lucide-react";
import { Field, BigButton } from "@/components/Field";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/config/modules";

export const Card = ({ title, children, testId }) => (
  <section className="bg-white rounded-3xl border border-[#E2DDD3] p-5 sm:p-8 fade-up" data-testid={testId}>
    <h2 className="text-base md:text-lg font-bold mb-5">{title}</h2>
    {children}
  </section>
);

export const ProfileSection = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user.name, phone: user.phone || "" });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/auth/profile", form); setUser(data); toast.success("Perfil atualizado"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title="Meu perfil" testId="settings-profile">
      <form onSubmit={save} className="space-y-4">
        <Field label="Nome" id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" />
        <Field label="E-mail" id="p-email" value={user.email} disabled data-testid="profile-email-input" />
        <Field label="Telefone" id="p-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone-input" />
        <BigButton type="submit" data-testid="profile-save-button">Salvar perfil</BigButton>
      </form>
    </Card>
  );
};

export const PasswordSection = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", form); toast.success("Senha alterada"); setForm({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title="Alterar senha" testId="settings-password">
      <form onSubmit={save} className="space-y-4">
        <Field label="Senha atual" id="c-pw" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" />
        <Field label="Nova senha" id="n-pw" type="password" minLength={6} required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" />
        <BigButton type="submit" variant="earth" data-testid="password-save-button">Alterar senha</BigButton>
      </form>
    </Card>
  );
};

export const BrandSection = () => {
  const { appName, logoUrl, whatsapp, setSettings } = useSettings();
  const [name, setName] = useState(appName);
  const [wpp, setWpp] = useState(whatsapp || "");
  useEffect(() => setWpp(whatsapp || ""), [whatsapp]);
  const fileRef = useRef();
  useEffect(() => setName(appName), [appName]);

  const save = async (body) => {
    try { const { data } = await api.put("/settings", body); setSettings(data); toast.success("Identidade atualizada"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) return toast.error("Imagem muito grande (máx. 1MB)");
    const r = new FileReader();
    r.onload = () => save({ logo_url: r.result });
    r.readAsDataURL(f);
  };

  return (
    <Card title="Identidade do aplicativo" testId="settings-brand">
      <div className="rounded-2xl bg-[#F4F1EA] border border-dashed border-[#C86D51]/50 p-5 mb-5" data-testid="logo-preview"><Logo size="lg" /></div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onFile} data-testid="logo-file-input" />
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <BigButton type="button" variant="outline" onClick={() => fileRef.current.click()} data-testid="logo-upload-button"><Upload className="h-5 w-5" /> Enviar logomarca</BigButton>
        {logoUrl && <BigButton type="button" variant="outline" onClick={() => save({ logo_url: "" })} data-testid="logo-remove-button"><Trash2 className="h-5 w-5" /> Remover logo</BigButton>}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); save({ app_name: name }); }} className="space-y-4">
        <Field label="Nome do aplicativo" id="app-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="app-name-input" />
        <BigButton type="submit" data-testid="app-name-save-button">Salvar nome</BigButton>
      </form>
      <form onSubmit={(e) => { e.preventDefault(); save({ whatsapp_empresa: wpp }); }} className="space-y-4 mt-6">
        <Field label="WhatsApp da empresa (botão Tenho interesse)" id="wpp-emp" type="tel" placeholder="(64) 99999-0000" value={wpp} onChange={(e) => setWpp(e.target.value)} data-testid="whatsapp-empresa-input" />
        <BigButton type="submit" variant="earth" data-testid="whatsapp-empresa-save">Salvar WhatsApp</BigButton>
      </form>
    </Card>
  );
};

export const PermissionsMatrix = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/modules/matrix").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <Card title="Matriz de permissões" testId="settings-permissions">
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm min-w-[420px]">
          <thead><tr className="text-left text-[#5C6B66]">
            <th className="p-2">Módulo</th>{["admin", "tecnico", "financeiro", "cliente"].map((r) => <th key={r} className="p-2 text-center">{ROLE_LABEL[r].split("/")[0]}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-[#E2DDD3]" data-testid={`perm-row-${m.id}`}>
                <td className="p-2 font-medium">{m.label}{m.status === "soon" && <span className="ml-2 text-[10px] uppercase text-[#B05A40] font-bold">em breve</span>}</td>
                {["admin", "tecnico", "cliente"].map((r) => (
                  <td key={r} className="p-2 text-center">{m.roles.includes(r) ? <Check className="h-5 w-5 mx-auto text-[#2E7D32]" /> : <Minus className="h-5 w-5 mx-auto text-[#C9C2B4]" />}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
